import {
  HEART_FRONT_DEFINITIONS,
  type HeartFrontId
} from "./heart-front-definition";
import {
  generateHeartProvinceTopology,
  heartProvinceCellById
} from "./heart-province-topology";

/**
 * Minimal server-owned position required to select a Heart Front entry.
 *
 * Keep this pure registry independent from persistence-backed placement details:
 * callers remain structurally compatible while other consumers import only this
 * minimal shape.
 */
export interface HeartFrontServerPosition {
  readonly sectorId: string;
  readonly ring: number;
  readonly sector: number;
  readonly q: number;
  readonly r: number;
}

const HEART_FRONT_COUNT = 6;
const HEART_FRONT_ENTRY_RING = 1;
const HEART_FRONT_ENTRY_RADIUS = 5;
const HEART_FRONT_ARRIVAL_SLOT = 6;

export interface HeartFrontEntry {
  readonly frontId: HeartFrontId;
  readonly approachSectorId: string;
  readonly arrivalId: string;
  readonly q: number;
  readonly r: number;
}

function invalidRegistry(): never {
  throw new Error("heart_front_entry_registry_invalid");
}

function buildHeartFrontEntries(): readonly HeartFrontEntry[] {
  if (HEART_FRONT_DEFINITIONS.length !== HEART_FRONT_COUNT) {
    return invalidRegistry();
  }

  const topology = generateHeartProvinceTopology();
  const entries: HeartFrontEntry[] = [];
  const frontIds = new Set<string>();
  const arrivalIds = new Set<string>();

  for (let index = 0; index < HEART_FRONT_COUNT; index += 1) {
    const front = index + 1;
    const definition = HEART_FRONT_DEFINITIONS[index];
    if (
      definition.front !== front ||
      definition.frontId !== `heart-front-${front}` ||
      frontIds.has(definition.frontId) ||
      arrivalIds.has(definition.arrivalId)
    ) {
      return invalidRegistry();
    }

    const arrival = heartProvinceCellById(topology, definition.arrivalId);
    if (
      arrival === undefined ||
      arrival.role !== "arrival" ||
      arrival.front !== front ||
      arrival.slot !== HEART_FRONT_ARRIVAL_SLOT ||
      arrival.distance !== 12
    ) {
      return invalidRegistry();
    }

    frontIds.add(definition.frontId);
    arrivalIds.add(definition.arrivalId);
    entries.push(Object.freeze({
      frontId: definition.frontId,
      approachSectorId: `S1_${index}`,
      arrivalId: definition.arrivalId,
      q: arrival.q,
      r: arrival.r
    }));
  }

  if (frontIds.size !== HEART_FRONT_COUNT || arrivalIds.size !== HEART_FRONT_COUNT) {
    return invalidRegistry();
  }
  return Object.freeze(entries);
}

const HEART_FRONT_ENTRIES = buildHeartFrontEntries();

export function heartFrontEntryForFrontId(
  frontId: HeartFrontId
): HeartFrontEntry | undefined {
  return HEART_FRONT_ENTRIES.find((entry) => entry.frontId === frontId);
}

function isInnerRingPosition(value: unknown): value is HeartFrontServerPosition {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const source = value as Record<string, unknown>;
  const { sectorId, sector, q, r } = source;
  if (
    source.ring !== HEART_FRONT_ENTRY_RING ||
    typeof sectorId !== "string" ||
    typeof sector !== "number" ||
    !Number.isSafeInteger(sector) ||
    sector < 0 ||
    sector >= HEART_FRONT_COUNT ||
    sectorId !== `S1_${sector}` ||
    typeof q !== "number" ||
    typeof r !== "number" ||
    !Number.isSafeInteger(q) ||
    !Number.isSafeInteger(r)
  ) {
    return false;
  }

  return Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r)) <= HEART_FRONT_ENTRY_RADIUS;
}

export function heartFrontEntryForServerPosition(
  position: HeartFrontServerPosition
): HeartFrontEntry | undefined {
  if (!isInnerRingPosition(position)) return undefined;
  return HEART_FRONT_ENTRIES[position.sector];
}
